import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useGetShipmentOperationCountQuery } from "store/services/dashboardApi";
import { TopBarLabel, barValueLabel } from "./ChartControls";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const ShipmentOperationsChart = () => {
  const chartRef = useRef(null);
  const theme = useTheme();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const menuTimeout = useRef(null);

  const {
    data: operationCounts = {},
    isLoading,
    isError,
  } = useGetShipmentOperationCountQuery();

  // Modern color palette for distinct, vibrant colors
  const modernColors = [
    "#4F46E5", // Indigo
    "#06B6D4", // Cyan
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#EF4444", // Red
    "#8B5CF6", // Violet
    "#F97316", // Orange
  ];

  const chartData = [
    {
      operation: "Pending",
      count: operationCounts.pendingCount || 0,
      color: modernColors[0],
    },
    {
      operation: "Collected",
      count: operationCounts.collectedCount || 0,
      color: modernColors[0],
    },
    {
      operation: "Consolidation",
      count: operationCounts.consolidationCount || 0,
      color: modernColors[1],
    },
    {
      operation: "Loaded",
      count: operationCounts.loadedCount || 0,
      color: modernColors[2],
    },
    {
      operation: "Unloaded",
      count: operationCounts.unloadedCount || 0,
      color: modernColors[3],
    },
    {
      operation: "Deconsolidation",
      count: operationCounts.deconsolidationCount || 0,
      color: modernColors[4],
    },
    {
      operation: "Send Out",
      count: operationCounts.sendOutCount || 0,
      color: modernColors[5],
    },
    {
      operation: "Warehousing",
      count: operationCounts.warehousingCount || 0,
      color: modernColors[6],
    },
  ];

  // Extract labels and data for the chart
  const operations = chartData.map((item) => item.operation);
  const counts = chartData.map((item) => item.count);

  // Calculate maximum value for Y-axis
  const maxCount = Math.max(...counts);
  const calculatedMax = maxCount + Math.ceil(maxCount * 0.2); 
  const maxValue = calculatedMax > 10 ? calculatedMax : 10;

  // Create series data
  const series = [
    {
      data: counts,
      label: "Shipment Operations",
    },
  ];

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    clearTimeout(menuTimeout.current);
    menuTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 3000);
  };

  const handleMenuClose = () => {
    clearTimeout(menuTimeout.current);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (open) {
      clearTimeout(menuTimeout.current);
      menuTimeout.current = setTimeout(() => {
        setAnchorEl(null);
      }, 5000);
    }
    return () => clearTimeout(menuTimeout.current);
  }, [open]);

  const handleFullScreen = () => {
    if (chartRef.current) {
      if (chartRef.current.requestFullscreen) {
        chartRef.current.requestFullscreen();
      }
      setIsFullScreen(true);
    }
    handleMenuClose();
  };

  const handleMinimize = () => {
    document.exitFullscreen();
    setIsFullScreen(false);
  };

  useEffect(() => {
    const exitHandler = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setIsFullScreen(false);
        try {
          if (window.screen?.orientation?.unlock) {
            window.screen.orientation.unlock();
          }
        } catch (e) {}
      }
    };
    document.addEventListener("fullscreenchange", exitHandler);
    document.addEventListener("webkitfullscreenchange", exitHandler);
    return () => {
      document.removeEventListener("fullscreenchange", exitHandler);
      document.removeEventListener("webkitfullscreenchange", exitHandler);
    };
  }, []);

  const handleDownload = async (format) => {
    if (!chartRef.current) return;

    const canvas = await html2canvas(chartRef.current, {
      backgroundColor: getComputedStyle(chartRef.current).backgroundColor,
      scale: 2,
      useCORS: true,
    });

    const image = canvas.toDataURL("image/png");

    if (format === "pdf") {
      const pdf = new jsPDF("landscape", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(image, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("shipment_operations_chart.pdf");
    } else {
      const link = document.createElement("a");
      link.href = image;
      link.download = `shipment_operations_chart.${format}`;
      link.click();
    }
  };

  if (isLoading) return <div>Loading operations chart...</div>;
  if (isError) return <div>Failed to load operations data</div>;

  return (
    <Box
      ref={chartRef}
      sx={{
        mt: 2,
        width: isFullScreen ? "100vw" : "100%",
        height: isFullScreen ? "100vh" : "auto",
        minHeight: isFullScreen ? "100vh" : "auto",
        padding: 2,
        boxShadow: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        position: "relative",
        zIndex: isFullScreen ? 9999 : "auto",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
        >
          Shipment Operations Overview
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          {isFullScreen && (
            <IconButton onClick={handleMinimize} title="Exit Fullscreen">
              <FullscreenExitIcon />
            </IconButton>
          )}
          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
            {!isFullScreen && (
              <MenuItem onClick={handleFullScreen}>View Full Screen</MenuItem>
            )}
            <MenuItem onClick={() => handleDownload("png")}>
              <GetAppIcon sx={{ mr: 1 }} /> Download PNG
            </MenuItem>
            <MenuItem onClick={() => handleDownload("jpeg")}>
              <GetAppIcon sx={{ mr: 1 }} /> Download JPEG
            </MenuItem>
            <MenuItem onClick={() => handleDownload("pdf")}>
              <PictureAsPdfIcon sx={{ mr: 1 }} /> Download PDF
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Grid
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Box sx={{ width: "100%", height: isFullScreen ? "calc(100vh - 220px)" : 400 }}>
          <BarChart
            xAxis={[
              {
                scaleType: "band",
                data: operations,
                tickLabelStyle: {
                  angle: -45,
                  textAnchor: "end",
                },
              },
            ]}
            yAxis={[
              {
                min: 0,
                max: maxValue,
              },
            ]}
            series={series}
            barLabel={barValueLabel}
            slots={{ barLabel: TopBarLabel }}
            margin={{
              left: 80,
              right: 30,
              top: 30,
              bottom: 100,
            }}
            colors={modernColors}
            tooltip={{
              trigger: "item",
            }}
            slotProps={{
              tooltip: {
                style: {
                  zIndex: isFullScreen ? 999999 : 1000,
                  position: isFullScreen ? "fixed" : "absolute",
                  pointerEvents: "none",
                },
              },
            }}
          />
        </Box>
      </Grid>
    </Box>
  );
};

export default ShipmentOperationsChart;
